import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth-context";
import { api, fmtCurrency } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Plus, ArrowUp, History, ChevronDown } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";

export default function BusinessFinance() {
    const { user } = useAuth();
    const [wallet, setWallet] = useState(null);
    const [overview, setOverview] = useState(null);
    const [txns, setTxns] = useState([]);

    useEffect(() => {
        api.get("/wallet").then((r) => setWallet(r.data));
        api.get("/business/overview").then((r) => setOverview(r.data));
        api.get("/wallet/transactions").then((r) => setTxns(r.data));
    }, []);

    const cf = (overview?.cashflow || []).map((m) => ({ month: m.month, sales: m.sales, expenses: m.expenses }));

    return (
        <div data-testid="business-finance-page">
            <MobileHeader title="Finance" subtitle="Wallet, cashflow & reports" backTo="/business" variant="blue">
                <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl p-4">
                    <div className="text-xs uppercase tracking-wider text-white/70">Wallet Balance</div>
                    <div className="font-display text-3xl font-bold mt-1" data-testid="biz-wallet-balance">
                        {wallet ? fmtCurrency(wallet.balance, wallet.currency, { minDecimals: 2, maxDecimals: 2 }) : "—"}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="bg-white/15 rounded-lg p-2 text-center">
                            <Plus size={16} className="mx-auto mb-1" />
                            <div className="text-[10px] font-bold">Add Money</div>
                        </div>
                        <div className="bg-white/15 rounded-lg p-2 text-center">
                            <ArrowUp size={16} className="mx-auto mb-1" />
                            <div className="text-[10px] font-bold">Pay</div>
                        </div>
                        <div className="bg-white/15 rounded-lg p-2 text-center">
                            <History size={16} className="mx-auto mb-1" />
                            <div className="text-[10px] font-bold">Transfer</div>
                        </div>
                    </div>
                </div>
            </MobileHeader>

            <div className="px-5 mt-4 space-y-4 pb-4">
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                    <div className="flex items-center justify-between mb-2">
                        <div className="font-display font-semibold text-[#0A1930]">Cashflow</div>
                        <div className="text-xs font-semibold text-[#0066FF] flex items-center gap-1">Last 6 Months <ChevronDown size={12} /></div>
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                        <AreaChart data={cf}>
                            <defs>
                                <linearGradient id="cf-sales" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0066FF" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#0066FF" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="month" axisLine={false} tickLine={false} fontSize={11} />
                            <YAxis hide />
                            <Tooltip formatter={(v) => fmtCurrency(v, "NGN", { compact: true })} />
                            <Area type="monotone" dataKey="sales" stroke="#0066FF" strokeWidth={2.5} fill="url(#cf-sales)" />
                            <Line type="monotone" dataKey="expenses" stroke="#FF6B00" strokeWidth={2} dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="font-display font-semibold text-[#0A1930] mb-3">Recent Transactions</div>
                    <div className="space-y-2">
                        {txns.slice(0, 8).map((t) => (
                            <div key={t.id} className="flex items-center gap-3" data-testid={`biz-txn-${t.id}`}>
                                <div className={`size-9 rounded-lg flex items-center justify-center ${t.type === "credit" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                                    {t.type === "credit" ? "↓" : "↑"}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm truncate">{t.description}</div>
                                    <div className="text-xs text-slate-500 capitalize">{t.category}</div>
                                </div>
                                <div className={`font-bold text-sm ${t.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                                    {t.type === "credit" ? "+" : "−"}{fmtCurrency(t.amount, "NGN", { compact: true })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
